/*

? Required Imports

*/

import { InfoOutlined, KeyboardArrowDown, KeyboardArrowRight, Refresh } from "@mui/icons-material";
import {
	CircularProgress,
	Dialog,
	DialogContent,
	DialogTitle,
	Divider,
	FormControl,
	Link,
	MenuItem,
	Select,
	SvgIcon,
	TextField,
	Typography,
} from "@mui/material";
import { styled } from "@mui/system";
import { AE, IN, SG, UM } from "country-flag-icons/react/3x2";
import { getSession, signIn } from "next-auth/react";
import { useRouter } from "next/router";
import React from "react";
import { constructUserDisplayNameString, UserDisplayNameStringType } from "../../../lib/auth/helpers";
import getCountry, {
	CountryCodeOption,
	CountryCodeOptionInterface,
	countryCodeOptions,
} from "../../../lib/data/Countries";
import calculateLineHeight from "../../../lib/ui-helpers/CalculateLineHeight";
import { GetReferrerDetailsAPIResponseType } from "../../../pages/api/referrer/[...referrer]";
import StateInterface from "../../../redux-magic/state-interface";
import {
	setLoginFlowGlobalStateModalOpenThunk,
	setLoginFlowGlobalStateUiSnackbarMessageThunk,
	setLoginFlowGlobalStateUiSnackbarOpenThunk,
	setLoginFlowGlobalStateUiSnackbarTypeThunk,
	setLoginFlowGlobalStateUserCountryCodeThunk,
	setLoginFlowGlobalStateUserDisplayNameThunk,
	setLoginFlowGlobalStateUserFirstNameThunk,
	setLoginFlowGlobalStateUserIsSignedInThunk,
	setLoginFlowGlobalStateUserIsSignInThunk,
	setLoginFlowGlobalStateUserIsSignUpThunk,
	setLoginFlowGlobalStateUserLastNameThunk,
	setLoginFlowGlobalStateUserOTPThunk,
	setLoginFlowGlobalStateUserPhoneThunk,
	setLoginFlowGlobalStateUserUserIdThunk,
	updateReduxSessionThunk,
} from "../../../redux-magic/store";
import BeegruButton from "../../common-components/buttons/BeegruButton";

/*

? Data Imports
* Only import the country flag icons for the countries we support
^ IN, AE, SG, UM

*/

interface CountryFlagProps {
	width?: number;
	children?: React.ReactNode;
}

interface FlagSVGIconProps {
	theme: any;
	calculatedWidth: number;
	calculatedHeight: number;
}

/*

& Make sure to add the separately imported country flag icons to the AllFlags object

*/

const AllFlags = {
	IN,
	AE,
	SG,
	UM,
};

const defaultCountryCode: string = "+91";
const defaultIsIndia: boolean = true;
const defaultFlag: JSX.Element = <AllFlags.IN />;
const defaultFlagWidth: number = 34.5;

/*

& Static Constant Definitions

*/

const defaultResendOTPTimerValue: number = 30; // Default timer value for the resend OTP button

/*

& Style Definitions

*/

const StyledModal = styled(Dialog)(({ theme }) => ({
	"& .MuiPaper-root": {
		position: "absolute",
		bottom: 0,
		left: 0,
		width: "100%",
		maxWidth: "100%",
		margin: 0,
		borderRadius: "1rem 1rem 0rem 0rem",
		[theme.breakpoints.up("sm")]: {
			position: "relative",
			width: "100%",
			maxWidth: "30rem",
			margin: 0,
			borderRadius: "1rem 1rem 1rem 1rem",
		},
	},
})) as typeof Dialog;

const E164PhoneContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "flex-start",
	alignItems: "flex-start",
	width: "100%",
	gap: theme.spacing(1),
}));

const CountryCodeFormControl = styled(FormControl)(({ theme }) => ({
	flex: "0 0 5.25rem",
}));

const CountryCodeSelect = styled(Select)(({ theme }) => ({
	height: "3.5rem",
}));

const PhoneTextField = styled(TextField)(({ theme }) => ({
	flex: "1 1 auto",
	"& .MuiFormHelperText-root": {
		marginLeft: "-5.25rem",
	},
}));

const KeyboardArrowDownIcon = styled(KeyboardArrowDown)(({ theme }) => ({
	flex: "0 0 24px",
	width: "24px",
	height: "24px",
	fontSize: "24px",
	color: theme.palette.text.secondary,
})) as any;

const FlagSVGIcon = styled(SvgIcon, {
	shouldForwardProp: (prop) => prop !== "calculatedWidth" && prop !== "calculatedHeight",
})<FlagSVGIconProps>(({ theme, calculatedWidth, calculatedHeight }: FlagSVGIconProps) => ({
	marginRight: "3.5px",
	width: calculatedWidth + "px",
	height: calculatedHeight + "px",
	borderRadius: "0.125rem",
})) as any;

const CountryCodeMenuItem = styled(MenuItem)(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "flex-start",
	alignItems: "center",
	whiteSpace: "wrap",
	maxWidth: "15rem",
	minWidth: "15rem",
	gap: theme.spacing(1),
	paddingLeft: theme.spacing(1),
	paddingRight: theme.spacing(1),
})) as typeof MenuItem;
const CountryCodeMenuItemText = styled(Typography)(({ theme }) => ({
	fontSize: "0.875rem",
	lineHeight: calculateLineHeight("0.875rem"),
	maxWidth: "calc(100% - 3.375rem)",
	minWidth: "calc(100% - 3.375rem)",
	fontStyle: "normal",
	fontWeight: 400,
	letterSpacing: "0.15px",
	color: theme.palette.text.primary,
})) as typeof Typography;
const SendOTPButton = styled(BeegruButton)(({ theme }) => ({
	flex: "1 1 auto",
})) as any;

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "center",
	alignItems: "flex-start",
	gap: theme.spacing(1),
})) as typeof DialogContent;

const OTPTextField = styled(TextField)(({ theme }) => ({
	flex: "1 1 auto",
	width: "100%",
})) as typeof TextField;

const ActionButtonsContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-around",
	alignItems: "center",
	width: "100%",
	gap: theme.spacing(1),
	marginTop: theme.spacing(1),
})) as any;

const VerifyOTPButton = styled(BeegruButton)(({ theme }) => ({
	flex: "1 1 auto",
})) as any;

const InfoItemLink = styled(Link)(({ theme }) => ({
	flex: "1 1 auto",
	fontSize: "0.75rem",
	lineHeight: "1rem",
	fontStyle: "normal",
	fontWeight: 400,
	letterSpacing: "0.15px",
	color: theme.palette.primary.main,
	"&:hover": {
		cursor: "pointer",
	},
})) as typeof Link;

const InfoItemIcon = styled(InfoOutlined)(({ theme }) => ({
	flex: "0 0 24px",
	width: "1.5rem",
	height: "1.5rem",
	fontSize: "1.5rem",
	color: theme.palette.text.secondary,
})) as any;

const InfoItemText = styled(Typography)(({ theme }) => ({
	flex: "1 1 auto",
	fontSize: "0.75rem",
	lineHeight: "1rem",
	fontStyle: "normal",
	fontWeight: 400,
	letterSpacing: "0.15px",
	color: theme.palette.text.secondary,
})) as typeof Typography;

const InfoItemsContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	alignItems: "center",
	justifyContent: "flex-start",
	width: "100%",
	gap: theme.spacing(1),
	marginTop: theme.spacing(1),
})) as any;

const FormAndActionDivider = styled(Divider)(({ theme }) => ({
	width: "calc(100% - 2rem)",
	marginLeft: theme.spacing(2),
	marginRight: theme.spacing(0),
	marginTop: theme.spacing(0),
	marginBottom: theme.spacing(0),
})) as typeof Divider;

/*

& Sub Component Definitions

*/

const CountryFlag = ({ width, children }: CountryFlagProps) => {
	const height: number = ((width || 24) * 2) / 3; // Aspect ratio of 3:2
	return (
		<FlagSVGIcon
			inheritViewBox={true}
			calculatedWidth={width}
			calculatedHeight={height}
		>
			{children || defaultFlag}
		</FlagSVGIcon>
	);
};

/*

& Local function to build the snackbar message

*/

const buildSnackbarMessage: (
	userDisplayNameString: string,
	referrerDisplayNameString: string,
	action: "sign_up" | "sign_in" | "sign_out" | "sign_up_reward" | "default",
) => string = (
	userDisplayNameString: string,
	referrerDisplayNameString: string,
	action: "sign_up" | "sign_in" | "sign_out" | "sign_up_reward" | "default" = "default",
) => {
	switch (action) {
		case "sign_up":
			return `Welcome to Beegru, ${userDisplayNameString}!`;
		case "sign_in":
			return `Welcome back, ${userDisplayNameString}!`;
		case "sign_out":
			return `Logged out successfully!`;
		case "sign_up_reward":
			return `Congratulations ${userDisplayNameString}! You've earned 0.2 Beegru Points for signing up with ${referrerDisplayNameString}'s referral code!`;
		default:
			return "";
	}
};

/*

& Component Definition

*/

const LoginModal = ({
	login_flow_global_state,
	dispatch,
	session,
	is_sign_out,
}: {
	login_flow_global_state?: StateInterface["login_flow_global_state"];
	dispatch: Function;
	session: any;
	is_sign_out: boolean;
}) => {
	/*

	& Local states that affect the UI

	*/

	const [countryFlag, setCountryFlag] = React.useState<JSX.Element>(defaultFlag); // The flag icon shown in the country code dropdown
	const [title, setTitle] = React.useState<string>("Login / Sign up"); // The title of the modal
	const [sendOTPButtonText, setSendOTPButtonText] = React.useState<string>("Request OTP"); // The text shown on the "Request OTP" button
	const [sendOTPButtonDisabled, setSendOTPButtonDisabled] = React.useState<boolean>(true); // Is the "Request OTP" button disabled? Updates when user enters a phone number or when the OTP is sent and the timeout is reached
	const [e164FieldDisabled, setE164FieldDisabled] = React.useState<boolean>(false); // Is the E164 field disabled? Updates when user enters a phone number or when the OTP is sent and the timeout is reached
	const [otpFieldDisabled, setOtpFieldDisabled] = React.useState<boolean>(true); // Is the OTP field disabled? Updates when user enters a phone number or when the OTP is sent and the timeout is reached
	const [verifyOTPButtonText, setVerifyOTPButtonText] = React.useState<string>("Verify & Login"); // The text shown on the "Verify OTP" button
	const [verifyOTPButtonDisabled, setVerifyOTPButtonDisabled] = React.useState<boolean>(true); // Is the "Verify OTP" button disabled? Updates when user enters a phone number or when the OTP is sent and the timeout is reached
	const [verifyOTPButtonEndIcon, setVerifyOTPButtonEndIcon] = React.useState<JSX.Element>(<KeyboardArrowRight />); // The icon shown on the "Verify OTP" button
	const [e164Error, setE164Error] = React.useState<boolean>(false); // Form validation error for the E164 field
	const [e164HelperText, setE164HelperText] = React.useState<string>(""); // Form validation helper text for the E164 field
	const [otpError, setOtpError] = React.useState<boolean>(false); // Form validation error for the OTP field
	const [otpHelperText, setOtpHelperText] = React.useState<string>(""); // Form validation helper text for the OTP field

	/*

	& Local states that control the flow of the component

	*/

	const [rlt, setRlt] = React.useState<string>(""); // Rate Limit Token
	const [isSignUp, setIsSignUp] = React.useState<boolean>(false); // Is this a sign up flow? Updates when user requests OTP
	const [isSignIn, setIsSignIn] = React.useState<boolean>(false); // Is this a sign in flow? Updates when user requests OTP
	const [messageId, setMessageId] = React.useState<string>(""); // Message ID for the OTP request
	const [isRetry, setIsRetry] = React.useState<boolean>(false); // Is this a retry request? Updates when user requests OTP again
	const [resendOTPTimerValue, setResendOTPTimerValue] = React.useState<number>(defaultResendOTPTimerValue); // Timer value for the resend OTP button
	const [recaptchaToken, setRecaptchaToken] = React.useState<string>(""); // Recaptcha token for the OTP request
	const [pageLoad, setPageLoad] = React.useState<boolean>(true); // Changes to false when the page is loaded

	/*

	& Local refs that control the flow of the component

	*/

	const resendOTPTimerInterval = React.useRef<any>(null); // Timer interval for the resend OTP button
	const otpTextFieldInputRef = React.useRef<any>(null); // Ref for the OTP text field input
	const phoneTextFieldInputRef = React.useRef<any>(null); // Ref for the Phone text field input
	const sendOTPButtonRef = React.useRef<any>(null); // Ref for the Send OTP button
	const verifyOTPButtonRef = React.useRef<any>(null); // Ref for the Verify OTP button

	/*

	& Local hooks that control the flow of the component

	*/

	const router = useRouter(); // Next.js router

	// Focus the appropriate input field when the modal opens

	React.useEffect(() => {
		if (!e164FieldDisabled && login_flow_global_state?.ui.modal.open) {
			const to = setTimeout(() => phoneTextFieldInputRef.current?.focus(), 100);
			return () => clearTimeout(to);
		} else if (!otpFieldDisabled && otpTextFieldInputRef.current && login_flow_global_state?.ui.modal.open) {
			otpTextFieldInputRef.current.focus();
		}
	}, [
		phoneTextFieldInputRef,
		e164FieldDisabled,
		otpFieldDisabled,
		otpTextFieldInputRef,
		login_flow_global_state?.ui.modal.open,
	]);

	/*

	& Keep the user's display name in sync with the session

	*/

	React.useEffect(() => {
		if (login_flow_global_state?.user.is_signed_in && session !== null) {
			const UserDisplayNameStringProps: UserDisplayNameStringType = {
				country_code: login_flow_global_state?.user.country_code,
				phone: login_flow_global_state?.user.phone,
				first_name: login_flow_global_state?.user.first_name,
				last_name: login_flow_global_state?.user.last_name,
			};
			const user_display_name_string: string = constructUserDisplayNameString(UserDisplayNameStringProps);
			dispatch(setLoginFlowGlobalStateUserDisplayNameThunk(user_display_name_string));
		}
	}, [
		login_flow_global_state?.user.is_signed_in,
		login_flow_global_state?.user.country_code,
		login_flow_global_state?.user.phone,
		login_flow_global_state?.user.first_name,
		login_flow_global_state?.user.last_name,
		dispatch,
		session,
	]);

	/*

	& Update local sign in and sign up states

	*/

	React.useEffect(() => {
		setIsSignIn(login_flow_global_state?.user.is_sign_in || false);
		setIsSignUp(login_flow_global_state?.user.is_sign_up || false);
	}, [
		login_flow_global_state?.user.is_signed_in,
		login_flow_global_state?.user.is_sign_up,
		login_flow_global_state?.user.is_sign_in,
		setIsSignIn,
		setIsSignUp,
	]);

	/*

	& Asynchronously update the referrer's display name if the user signs up with a referral code

	*/

	React.useEffect(() => {
		if (
			login_flow_global_state?.user.is_sign_up &&
			login_flow_global_state?.user.is_signed_in &&
			login_flow_global_state?.user.has_referral_code &&
			login_flow_global_state?.user.referral_code &&
			session !== null
		) {
			const fetchReferrerDetails: (referrer_type: "user" | "bdx", referral_code: string) => Promise<void> = async (
				referrer_type: "user" | "bdx" = "user",
				referral_code: string = "",
			) => {
				const referrer_details_api_response: GetReferrerDetailsAPIResponseType = await (
					await fetch("/api/referrer/" + referrer_type + "/" + referral_code, {
						method: "GET",
						headers: {
							"Cache-Control": "no-cache, no-store, max-age=0, must-revalidate",
							Pragma: "no-cache",
							Expires: "0",
						},
					})
				).json();
				if (referrer_details_api_response.error === false) {
					/*

					& If there are no errors, update the referrer's details in the redux store

					*/
					dispatch(
						setLoginFlowGlobalStateUserCountryCodeThunk(referrer_details_api_response.referrer_details.country_code),
					);
					dispatch(setLoginFlowGlobalStateUserPhoneThunk(referrer_details_api_response.referrer_details.phone));
					dispatch(
						setLoginFlowGlobalStateUserFirstNameThunk(referrer_details_api_response.referrer_details.first_name),
					);
					dispatch(setLoginFlowGlobalStateUserLastNameThunk(referrer_details_api_response.referrer_details.last_name));
					dispatch(
						setLoginFlowGlobalStateUserDisplayNameThunk(
							constructUserDisplayNameString({
								country_code: referrer_details_api_response.referrer_details.country_code,
								phone: referrer_details_api_response.referrer_details.phone,
								first_name: referrer_details_api_response.referrer_details.first_name,
								last_name: referrer_details_api_response.referrer_details.last_name,
							}),
						),
					);
				}
				dispatch(setLoginFlowGlobalStateUiSnackbarTypeThunk("success"));
				dispatch(
					setLoginFlowGlobalStateUiSnackbarMessageThunk(
						buildSnackbarMessage(
							login_flow_global_state?.user.display_name,
							login_flow_global_state?.referrer.display_name,
							"sign_up_reward",
						),
					),
				);
			};
			/*

			& Trigger the asynchronous fetchReferrerDetails function

			*/

			fetchReferrerDetails(
				login_flow_global_state?.referrer.referrer_type || "user",
				login_flow_global_state?.user.referral_code || "",
			);
		}
	}, [
		login_flow_global_state?.user.is_sign_up,
		login_flow_global_state?.user.is_signed_in,
		login_flow_global_state?.user.referral_code,
		login_flow_global_state?.user.has_referral_code,
		login_flow_global_state?.referrer.referrer_type,
		login_flow_global_state?.referrer.display_name,
		login_flow_global_state?.user.display_name,
		dispatch,
		session,
	]);

	React.useEffect(() => {
		if (is_sign_out) {
			new Function(...(login_flow_global_state?.action.sign_out || ["return"]))();
			dispatch(setLoginFlowGlobalStateUiSnackbarTypeThunk("info"));
			dispatch(setLoginFlowGlobalStateUiSnackbarMessageThunk(buildSnackbarMessage("", "", "sign_out")));
			dispatch(setLoginFlowGlobalStateUiSnackbarOpenThunk(true));
		}
	}, [is_sign_out, login_flow_global_state?.action, dispatch]);

	/*

	& Other UI Handlers

	*/

	const renderCountryCodeInputSelectedValue = (value: string) => {
		return <CountryFlag width={defaultFlagWidth}>{countryFlag}</CountryFlag>;
	};

	/*

	& Click Event Handlers

	*/

	const onClickSendOTPButton: (event: any) => Promise<void> = async (event: any) => {
		setE164FieldDisabled(true);
		setSendOTPButtonDisabled(true);
		setSendOTPButtonText("Sending OTP...");
		let localRLT = rlt;
		let localIsSignUp = isSignUp;
		let localMessageId = messageId;
		let localTimer = defaultResendOTPTimerValue;
		let localIsRetry =
			sendOTPButtonText === "Resend OTP" ||
			sendOTPButtonText === "OTP ಮರುಕಳುಹಿಸಿ" ||
			sendOTPButtonText === "إعادة إرسال رمز التحقق";
		if (localIsRetry) {
			setIsRetry(true);
		}
		try {
			const getRltResponse = await fetch("/api/auth/get-rlt", {
				method: "GET",
				headers: {
					"Cache-Control": "no-cache, no-store, max-age=0, must-revalidate",
					Pragma: "no-cache",
					Expires: "0",
				},
			});
			if (getRltResponse.ok) {
				const getRltData = await getRltResponse.json();
				if (getRltData.rlt && getRltData.rlt.length > 0) {
					setRlt(getRltData.rlt);
					localRLT = getRltData.rlt;
				} else {
					throw new Error("failed_to_fetch_from_get_rlt");
				}
			}
		} catch (error: any) {
			console.error("Error fetching RLT:", error);
			return;
		}
		const sendOTPRequestBody = {
			country_code: login_flow_global_state?.user.country_code || defaultCountryCode,
			phone: login_flow_global_state?.user.phone || "",
			rlt: localRLT,
			is_retry: localIsRetry,
			message_id: messageId,
		};
		try {
			const sendOTPResponse = await fetch("/api/auth/send-otp", {
				method: "POST",
				headers: {
					"Cache-Control": "no-cache, no-store, max-age=0, must-revalidate",
					Pragma: "no-cache",
					Expires: "0",
					"Content-Type": "text/plain",
				},
				body: JSON.stringify(sendOTPRequestBody),
			});
			if (sendOTPResponse.ok) {
				const sendOTPData = await sendOTPResponse.json();
				if (sendOTPData.error !== null) {
					throw new Error(sendOTPData.error);
				}
				if (sendOTPData.rlt && sendOTPData.rlt.length > 0) {
					setRlt(sendOTPData.rlt);
					localRLT = sendOTPData.rlt;
				} else {
					throw new Error("failed_to_fetch_from_send_otp");
				}
				if (sendOTPData.message_id) {
					setMessageId(sendOTPData.message_id);
					localMessageId = sendOTPData.message_id;
				} else {
					throw new Error("failed_to_fetch_from_send_otp");
				}
				if (typeof sendOTPData.is_sign_up === "boolean") {
					setIsSignUp(sendOTPData.is_sign_up);
					setIsSignIn(!sendOTPData.is_sign_up);
					localIsSignUp = sendOTPData.is_sign_up;
					if (localIsSignUp) {
						setTitle("Sign up");
						dispatch(setLoginFlowGlobalStateUserIsSignUpThunk(true));
					} else {
						setTitle("Verify with OTP");
						dispatch(setLoginFlowGlobalStateUserIsSignInThunk(true));
					}
				} else {
					throw new Error("failed_to_fetch_from_send_otp");
				}
			} else {
				throw new Error("failed_to_send_otp");
			}
		} catch (error: any) {
			console.error("Error sending OTP:", error);
			return;
		}
		if (resendOTPTimerInterval.current) {
			clearInterval(resendOTPTimerInterval.current);
			setResendOTPTimerValue(defaultResendOTPTimerValue);
		}
		resendOTPTimerInterval.current = setInterval(() => {
			if (localTimer <= 0) {
				clearInterval(resendOTPTimerInterval.current);
				setSendOTPButtonText("Resend OTP");
				setSendOTPButtonDisabled(false);
				localTimer = defaultResendOTPTimerValue;
				return;
			}
			localTimer = localTimer - 1;
			setResendOTPTimerValue(localTimer);
			setSendOTPButtonText("Resend OTP in " + localTimer);
		}, 1000);
		setE164FieldDisabled(true);
		setVerifyOTPButtonDisabled(login_flow_global_state?.user.otp.length !== 6);
		setOtpFieldDisabled(false);
	};

	const onClickVerifyOTPButton: (event: any) => Promise<void> = async (event: any) => {
		setOtpFieldDisabled(true);
		setVerifyOTPButtonDisabled(true);
		setVerifyOTPButtonText("Signing in...");
		setVerifyOTPButtonEndIcon(
			<CircularProgress
				size={24}
				sx={{
					color: "#FFFFFF",
				}}
			/>,
		);
		setTitle("Signing in...");
		let localRecaptchaToken = recaptchaToken;
		try {
			// @ts-ignore
			localRecaptchaToken = await grecaptcha.enterprise.execute(process.env.RECAPTCHA_SITE_KEY_PRODUCTION || "", {
				action: "LOGIN",
			});
			setRecaptchaToken(localRecaptchaToken);
		} catch (error) {
			console.error(error);
		}
		const signInFunctionParameters = {
			callbackUrl: login_flow_global_state?.action.callback_url || "/en-IN/",
			redirect: false,
			country_code: login_flow_global_state?.user.country_code || defaultCountryCode,
			phone: login_flow_global_state?.user.phone || "",
			otp: login_flow_global_state?.user.otp || "",
			recaptcha_token: localRecaptchaToken,
			referral_code: login_flow_global_state?.user.has_referral_code ? login_flow_global_state?.user.referral_code : "",
		};
		const signInResponse = (await signIn("credentials", signInFunctionParameters)) || {
			error: "failed_to_fetch_from_sign_in",
			status: 401,
			ok: false,
			url: "/auth/login?error=failed_to_fetch_from_sign_in",
		};
		if (signInResponse.ok && signInResponse.url !== null) {
			const currentSession: any = await getSession();
			const currentUserId: string = currentSession?.user?.id || "";
			dispatch(updateReduxSessionThunk(currentSession));
			dispatch(setLoginFlowGlobalStateUserUserIdThunk(currentUserId));
			dispatch(setLoginFlowGlobalStateUserIsSignedInThunk(true));
			if (isSignIn && !isSignUp) {
				new Function(...(login_flow_global_state?.action.sign_in || ["session", "return"])).call(currentSession);
				dispatch(setLoginFlowGlobalStateUiSnackbarTypeThunk("success"));
				const userDisplayNameString: string = constructUserDisplayNameString({
					country_code: currentSession?.user?.dbUser.country_code,
					phone: currentSession?.user?.dbUser.phone,
					first_name: currentSession?.user?.dbUser.first_name,
					last_name: currentSession?.user?.dbUser.last_name,
				});
				dispatch(
					setLoginFlowGlobalStateUiSnackbarMessageThunk(
						buildSnackbarMessage(
							userDisplayNameString,
							login_flow_global_state?.referrer.display_name || "",
							"sign_in",
						),
					),
				);
				dispatch(setLoginFlowGlobalStateUiSnackbarOpenThunk(true));
			} else if (!isSignIn && isSignUp) {
				new Function(...(login_flow_global_state?.action.sign_up || ["session", "return"])).call(currentSession);
				dispatch(setLoginFlowGlobalStateUiSnackbarTypeThunk("success"));
				const userDisplayNameString: string = constructUserDisplayNameString({
					country_code: currentSession?.user?.dbUser.country_code,
					phone: currentSession?.user?.dbUser.phone,
					first_name: currentSession?.user?.dbUser.first_name,
					last_name: currentSession?.user?.dbUser.last_name,
				});
				dispatch(
					setLoginFlowGlobalStateUiSnackbarMessageThunk(
						buildSnackbarMessage(
							userDisplayNameString,
							login_flow_global_state?.referrer.display_name || "",
							"sign_up",
						),
					),
				);
				dispatch(setLoginFlowGlobalStateUiSnackbarOpenThunk(true));
			} else if (is_sign_out) {
				new Function(...(login_flow_global_state?.action.sign_out || ["return"]))();
				dispatch(setLoginFlowGlobalStateUiSnackbarTypeThunk("info"));
				dispatch(setLoginFlowGlobalStateUiSnackbarMessageThunk(buildSnackbarMessage("", "", "sign_out")));
				dispatch(setLoginFlowGlobalStateUiSnackbarOpenThunk(true));
			}
			setTitle("Login / Sign up");
			setSendOTPButtonText("Request OTP");
			setSendOTPButtonDisabled(true);
			setE164FieldDisabled(false);
			setOtpFieldDisabled(true);
			setVerifyOTPButtonDisabled(true);
			setVerifyOTPButtonText("Verify & Login");
			setVerifyOTPButtonEndIcon(<KeyboardArrowRight />);
			setResendOTPTimerValue(defaultResendOTPTimerValue);
			setIsSignUp(false);
			setIsRetry(false);
			setMessageId("");
			setRlt("");
			setRecaptchaToken("");
			if (resendOTPTimerInterval.current) {
				clearInterval(resendOTPTimerInterval.current);
			}
			dispatch(setLoginFlowGlobalStateModalOpenThunk(false));
		} else {
			dispatch(updateReduxSessionThunk(null as any));
			dispatch(setLoginFlowGlobalStateUserUserIdThunk(""));
			dispatch(setLoginFlowGlobalStateUserIsSignedInThunk(false));
		}
	};

	return (
		<React.Fragment>
			<StyledModal
				open={login_flow_global_state?.ui.modal.open || false}
				onClose={() => {
					dispatch(setLoginFlowGlobalStateModalOpenThunk(false));
				}}
				disableRestoreFocus={true}
			>
				<DialogTitle>{title}</DialogTitle>
				<FormAndActionDivider />
				<StyledDialogContent>
					<E164PhoneContainer>
						<CountryCodeFormControl>
							<CountryCodeSelect
								id="country_code_select"
								name="country_code_select"
								inputProps={{
									id: "country_code",
									name: "country_code",
									required: true,
								}}
								MenuProps={{
									sx: {
										paddingTop: "0",
										paddingBottom: "0",
										paddingLeft: "0",
										paddingRight: "0",
										borderRadius: "0.5rem",
										"& .MuiMenu-paper": {
											paddingTop: "0",
											paddingBottom: "0",
											paddingLeft: "0",
											paddingRight: "0",
											borderRadius: "0.5rem",
										},
									},
									MenuListProps: {
										sx: {
											paddingTop: "0",
											paddingBottom: "0",
											paddingLeft: "0",
											paddingRight: "0",
											borderRadius: "0.5rem",
										},
									},
								}}
								IconComponent={KeyboardArrowDownIcon}
								renderValue={renderCountryCodeInputSelectedValue}
								required={true}
								defaultValue={login_flow_global_state?.user.country_code || "+91"}
								value={login_flow_global_state?.user.country_code || "+91"}
								onChange={(event: any) => {
									const country: CountryCodeOption = getCountry((event.target.value as string).replace("+", ""));
									if (country !== null) {
										setCountryFlag((AllFlags as any)[country.code]);
									} else {
										setCountryFlag(defaultFlag);
									}
									dispatch(setLoginFlowGlobalStateUserCountryCodeThunk(event.target.value));
								}}
								disabled={e164FieldDisabled}
								error={e164Error}
								sx={{
									display:
										(e164FieldDisabled && sendOTPButtonText !== "Sending OTP...") || !otpFieldDisabled
											? "none"
											: "flex",
								}}
							>
								{countryCodeOptions.map(({ code, name, call }: CountryCodeOptionInterface) => {
									const Flag = (AllFlags as any)[code];
									return (
										<CountryCodeMenuItem
											key={code}
											value={"+" + call}
										>
											<CountryFlag width={defaultFlagWidth}>
												<Flag />
											</CountryFlag>
											<CountryCodeMenuItemText
												component="p"
												variant="body1"
											>
												{name}
											</CountryCodeMenuItemText>
										</CountryCodeMenuItem>
									);
								})}
							</CountryCodeSelect>
						</CountryCodeFormControl>
						<PhoneTextField
							id="phone"
							name="phone"
							type="tel"
							variant="outlined"
							autoComplete="off"
							placeholder={
								router.locale === "kn-IN"
									? "ದೂರವಾಣಿ ಸಂಖ್ಯೆ "
									: router.locale === "ar-AE"
										? "رقم الجوال"
										: "Phone Number"
							}
							required={true}
							slotProps={{
								htmlInput: {
									inputMode: "numeric",
									pattern: "[0-9]*",
								},
							}}
							inputRef={phoneTextFieldInputRef}
							value={login_flow_global_state?.user.phone || ""}
							onChange={(event: any) => {
								if (!Number.isNaN(Number(event.target.value))) {
									//Only allow numbers
									if (login_flow_global_state?.user.country_code === "+91" && event.target.value.length <= 10) {
										// Don't allow more than 10 digits for India
										setE164Error(false);
										setE164HelperText("");
										setSendOTPButtonDisabled(false);
										dispatch(setLoginFlowGlobalStateUserPhoneThunk(event.target.value));
									} else if (login_flow_global_state?.user.country_code !== "+91" && event.target.value.length <= 15) {
										// Don't allow more than 15 digits for other countries
										setE164Error(false);
										setE164HelperText("");
										setSendOTPButtonDisabled(false);
										dispatch(setLoginFlowGlobalStateUserPhoneThunk(event.target.value));
									} else {
										setE164Error(true);
										setE164HelperText("Invalid phone number");
										setSendOTPButtonDisabled(true);
									}
								}
								if (event.target.value.length > 0) {
									if (
										(login_flow_global_state?.user.country_code === "+91" && event.target.value.length < 10) ||
										event.target.length <= 0
									) {
										setE164Error(true);
										setE164HelperText("Invalid phone number");
										setSendOTPButtonDisabled(true);
									} else {
										setE164Error(false);
										setE164HelperText("");
										setSendOTPButtonDisabled(false);
									}
								} else {
									setE164Error(true);
									setE164HelperText("Invalid phone number");
									setSendOTPButtonDisabled(true);
								}
							}}
							onKeyUp={(event) => {
								if (event.key === "Enter") {
									if (
										(login_flow_global_state?.user.phone.length || 0) > 6 &&
										(login_flow_global_state?.user.phone.length || 0) < 15
									) {
										if (
											login_flow_global_state?.user.phone.length === 10 &&
											login_flow_global_state?.user.country_code === "+91"
										) {
											setE164Error(false);
											setE164HelperText("");
											setSendOTPButtonDisabled(false);
											if (sendOTPButtonRef.current) {
												sendOTPButtonRef.current.click();
											}
										} else if (login_flow_global_state?.user.country_code !== "+91") {
											setE164Error(false);
											setE164HelperText("");
											setSendOTPButtonDisabled(false);
											if (sendOTPButtonRef.current) {
												sendOTPButtonRef.current.click();
											}
										}
									} else {
										setE164Error(true);
										setE164HelperText("Invalid phone number");
										setSendOTPButtonDisabled(true);
									}
								}
							}}
							disabled={e164FieldDisabled}
							error={e164Error}
							helperText={e164HelperText}
							sx={{
								display:
									(e164FieldDisabled && sendOTPButtonText !== "Sending OTP...") || !otpFieldDisabled ? "none" : "flex",
							}}
						/>
					</E164PhoneContainer>
					<OTPTextField
						id="otp"
						name="otp"
						type="text"
						autoComplete="one-time-code"
						inputMode="numeric"
						variant="outlined"
						required={true}
						slotProps={{
							htmlInput: {
								inputMode: "numeric",
								pattern: "[0-9]*",
							},
						}}
						inputRef={otpTextFieldInputRef}
						placeholder={
							router.locale === "kn-IN"
								? "ನಿಮ್ಮ ಫೋನಿನಿಂದ OTP ನಮೂದಿಸಿ"
								: router.locale === "ar-AE"
									? "أدخل رمز التحقق (OTP) المرسل إلى جوالك"
									: "Enter OTP from your phone"
						}
						value={login_flow_global_state?.user.otp || ""}
						onChange={(event: any) => {
							if (!Number.isNaN(Number(event.target.value))) {
								// Only allow numbers
								if (event.target.value.length <= 6) {
									// Don't allow more than 6 digits
									if (event.target.value.length < 6) {
										setOtpError(true);
										setOtpHelperText("Invalid OTP");
									} else {
										setOtpError(false);
										setOtpHelperText("");
										setVerifyOTPButtonDisabled(false);
									}
									dispatch(setLoginFlowGlobalStateUserOTPThunk(event.target.value));
								} else {
									setOtpError(true);
									setOtpHelperText("Invalid OTP");
								}
							}
						}}
						onKeyUp={(event) => {
							if (event.key === "Enter") {
								if (login_flow_global_state?.user.otp.length === 6) {
									// Check if the OTP is 6 digits long
									setOtpError(false);
									setOtpHelperText("");
									if (verifyOTPButtonRef.current) {
										verifyOTPButtonRef.current.click();
									}
								} else {
									setOtpError(true);
									setOtpHelperText("Invalid OTP");
								}
							}
						}}
						error={otpError}
						helperText={otpHelperText}
						disabled={otpFieldDisabled}
						sx={{
							display:
								otpFieldDisabled &&
								((!e164FieldDisabled && sendOTPButtonText === "Request OTP") ||
									(e164FieldDisabled && sendOTPButtonText === "Sending OTP..."))
									? "none"
									: "flex",
						}}
					/>
					<ActionButtonsContainer>
						<SendOTPButton
							variant={sendOTPButtonText === "Resend OTP" ? "outlined" : "contained"}
							color="primary"
							onClick={onClickSendOTPButton}
							disabled={sendOTPButtonDisabled || e164Error}
							startIcon={sendOTPButtonText === "Resend OTP" ? <Refresh /> : undefined}
							sx={{
								display: verifyOTPButtonText === "Signing in..." ? "none" : "flex",
							}}
							ref={sendOTPButtonRef}
						>
							{sendOTPButtonText}
						</SendOTPButton>
						<VerifyOTPButton
							variant="contained"
							color="primary"
							onClick={onClickVerifyOTPButton}
							disabled={verifyOTPButtonDisabled || otpError}
							endIcon={verifyOTPButtonEndIcon}
							sx={{
								display:
									verifyOTPButtonDisabled && verifyOTPButtonText !== "Signing in..." && otpFieldDisabled
										? "none"
										: "flex",
								"&:disabled": {
									backgroundColor: verifyOTPButtonText === "Signing in..." ? "#FC8019" : undefined,
								},
							}}
							ref={verifyOTPButtonRef}
						>
							{verifyOTPButtonText === "Signing in..." ? "" : verifyOTPButtonText}
						</VerifyOTPButton>
					</ActionButtonsContainer>
					<InfoItemsContainer
						sx={{
							display:
								e164FieldDisabled && (sendOTPButtonText !== "Sending OTP..." || !otpFieldDisabled) ? "none" : "flex",
						}}
					>
						<InfoItemIcon />
						<InfoItemText>
							{router.locale === "kn-IN"
								? "OTP ಕಳುಹಿಸಿ ಕ್ಲಿಕ್ ಮಾಡುವ ಮೂಲಕ ನೀವು  ಬೀಗ್ರೂ ನಿಂದ OTP SMS ಸಂದೇಶಗಳನ್ನು ಸ್ವೀಕರಿಸಲು ಸಮ್ಮತಿಸುತ್ತೀರಿ. "
								: router.locale === "ar-AE"
									? `بالنقر على "إرسال رمز التحقق"، فإنك توافق على تلقي رسائل نصية قصيرة تحتوي على رمز التحقق (OTP) من بيجرو. `
									: "By Clicking 'Send OTP' you are consenting to receive OTP SMS messages from Beegru. "}
							<InfoItemLink
								href={"/" + router.locale + "/faqs/dlt-consent"}
								underline="hover"
								component="a"
								variant="body2"
							>
								{router.locale === "kn-IN"
									? "ಇನ್ನಷ್ಟು ತಿಳಿಯಿರಿ"
									: router.locale === "ar-AE"
										? "اعرف المزيد"
										: "Learn More"}
							</InfoItemLink>
						</InfoItemText>
					</InfoItemsContainer>
					<InfoItemsContainer
						sx={{
							display: otpFieldDisabled && verifyOTPButtonText !== "Signing in..." ? "none" : "flex",
						}}
					>
						<InfoItemIcon />
						<InfoItemText>
							{router.locale === "kn-IN"
								? "ಈ ಸೈಟ್ ಅನ್ನು reCAPTCHA ರಕ್ಷಿಸುತ್ತದೆ ಮತ್ತು Google "
								: router.locale === "ar-AE"
									? "هذا الموقع محمي بواسطة reCAPTCHA و سياسة الخصوصية من Google. "
									: "This site is protected by reCAPTCHA and the Google "}
							<InfoItemLink
								href="https://policies.google.com/privacy"
								underline="hover"
								component="a"
								variant="body2"
							>
								{router.locale === "kn-IN"
									? "ಗೌಪ್ಯತಾ ನೀತಿ"
									: router.locale === "ar-AE"
										? "سياسة الخصوصية"
										: "Privacy Policy"}
							</InfoItemLink>
							{router.locale === "kn-IN" ? " ಮತ್ತು " : router.locale === "ar-AE" ? " و " : " and "}
							<InfoItemLink
								underline="hover"
								href="https://policies.google.com/terms"
								component="a"
								variant="body2"
							>
								{router.locale === "kn-IN"
									? " ಸೇವಾ ನಿಯಮಗಳು"
									: router.locale === "ar-AE"
										? "شروط الخدمة"
										: "Terms of Service"}
							</InfoItemLink>

							{router.locale === "kn-IN" ? "ಅನ್ವಯಿಸುತ್ತವೆ." : router.locale === "ar-AE" ? " تطبيق" : " apply."}
						</InfoItemText>
					</InfoItemsContainer>
				</StyledDialogContent>
			</StyledModal>
		</React.Fragment>
	);
};

/*

& Default Export

*/

export default LoginModal;
